import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { TabularSinkAdapter } from 'app/model/adapters/activities/copysink/tabular.sink.adapter';
import { SqlSink } from 'app/model/pipeline/copysink/sql.copysink';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { CopySinkAdapter } from './copysink.adapter';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { SharedModel } from 'shell-models-lib';

export const sqlSinkAdapts = {
    type: '*',
    writeBatchTimeout: '*',
    writeBatchSize: '*',
    preCopyScript: '*',
    sqlWriterTableType: '*',
    sqlWriterStoredProcedureName: '*',
    storedProcedureParameters: '*',
    maxConcurrentConnections: '*',
    storedProcedureTableTypeParameterName: '*',
    structure: '*',
    tableOption: '*',
    sqlWriterUseTableLock: '*',
    disableMetricsCollection: '*',
    writeBehavior: '*',
    upsertSettings: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlSink),
    asynchronous: true,
    adapts: sqlSinkAdapts
})
export class SqlSinkAdapter extends CopySinkAdapter<SqlSink, Model.SqlSink> {
    protected async _toResource(model: SqlSink): Promise<Model.SqlSink> {
        const result: Model.SqlSink = {
            type: Metamodel.modelOf(model).name
        };
        TabularSinkAdapter.toSinkResource(model, result);
        result.sqlWriterStoredProcedureName = ValueAdapter.toResource(model, SqlSink.WRITER_STORED_PROCEDURE_NAME);

        result.writeBehavior = ValueAdapter.toResource(model, SqlSink.WRITE_BEHAVIOR);
        const isExpressionWriteBehavior = SqlSink.WRITE_BEHAVIOR.templateValue(model) != null;

        switch (result.writeBehavior) {
            case writeBehaviorOptionValues.insert:
                result.sqlWriterStoredProcedureName = undefined;
                result.sqlWriterUseTableLock = ValueAdapter.toResource(model, SqlSink.SQL_WRITER_USE_TABLE_LOCK);
                break;
            case writeBehaviorOptionValues.upsert:
                result.sqlWriterStoredProcedureName = undefined;
                this._upsertToResource(result, model);
                result.sqlWriterUseTableLock = ValueAdapter.toResource(model, SqlSink.SQL_WRITER_USE_TABLE_LOCK);
                break;
            case writeBehaviorOptionValues.storedProcedure:
                result.writeBehavior = undefined;
                this._storedProcedureToResource(result, model);
                break;
            case writeBehaviorOptionValues.SCD2:
                this._upsertToResource(result, model);
                break;
            default:
                if (isExpressionWriteBehavior) {
                    result.sqlWriterUseTableLock = ValueAdapter.toResource(model, SqlSink.SQL_WRITER_USE_TABLE_LOCK);
                    this._upsertToResource(result, model);
                    this._storedProcedureToResource(result, model);
                }
                break;
        }

        result.tableOption = ValueAdapter.toResource(model, SqlSink.TABLE_OPTION);
        result.disableMetricsCollection = ValueAdapter.toResource(model, SqlSink.DISABLE_METRICS_COLLECTION);
        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected async _toModel(resource: Model.SqlSink, owner: Activity): Promise<SqlSink> {
        const result = Owned.create<SqlSink>(Metamodel.modelByName(resource.type), owner);
        TabularSinkAdapter.toSinkModel(resource, result);

        let isExpressionWriteBehavior = false;
        if (resource.writeBehavior === undefined) {
            delete result.writeBehavior;
        } else {
            ValueAdapter.toModel(result, SqlSink.WRITE_BEHAVIOR, resource.writeBehavior);
            isExpressionWriteBehavior = SqlSink.WRITE_BEHAVIOR.templateValue(result) != null;
        }

        if (isExpressionWriteBehavior) {
            this._upsertToModel(resource, result);
            this._storedProcedureToModel(resource, result, owner);
        } else if (resource.sqlWriterStoredProcedureName) {
            // cannot use `resource.writeBehavior == null` because of CW
            // also since sqlWriterStoredProcedureName is mandatory for SP, it's more accurate
            // not checking for parameterization, since CW doesn't support it
            result.writeBehavior = 'storedProcedure';
            this._storedProcedureToModel(resource, result, owner);
        } else if (resource.writeBehavior === writeBehaviorOptionValues.upsert) {
            this._upsertToModel(resource, result);
        } else if (resource.writeBehavior === writeBehaviorOptionValues.SCD2) {
            this._upsertToModel(resource, result);
        }

        ValueAdapter.toModel(result, SqlSink.TABLE_OPTION, resource.tableOption);
        if (resource.sqlWriterUseTableLock !== undefined) {
            ValueAdapter.toModel(result, SqlSink.SQL_WRITER_USE_TABLE_LOCK, resource.sqlWriterUseTableLock);
        } else {
            delete result.sqlWriterUseTableLock;
        }
        ValueAdapter.toModel(result, SqlSink.DISABLE_METRICS_COLLECTION, resource.disableMetricsCollection);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }

    private _upsertToResource(result: Model.SqlSink, model: SqlSink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        const upsertSettings = ValueAdapter.toResource(model, SqlSink.UPSERT_SETTINGS);
        if (upsertSettings?.type === 'Expression') {
            result.upsertSettings = upsertSettings;
        } else {
            const useTempDB = ValueAdapter.toResource(model, SqlSink.WRITE_BEHAVIOR_USE_TEMP_DB);
            result.upsertSettings = {
                useTempDB,
                keys: ValueAdapter.toResource(model, SqlSink.WRITE_BEHAVIOR_KEYS),
                interimSchemaName:
                    useTempDB === true
                        ? undefined
                        : ValueAdapter.toResource(model, SqlSink.WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME)
            };
        }
    }

    private _storedProcedureToResource(result: Model.SqlSink, model: SqlSink): void {
        result.sqlWriterTableType = ValueAdapter.toResource(model, SqlSink.WRITER_TABLE_TYPE);
        result.storedProcedureTableTypeParameterName = ValueAdapter.toResource(
            model,
            SqlSink.WRITER_TABLE_TYPE_PARAMETER_NAME
        );

        if (model.storedProcedureParameters != null && model.storedProcedureParameters.length > 0) {
            const params: { [propertyName: string]: Model.StoredProcedureParameter } = {};
            model.storedProcedureParameters.forEach((p) => {
                params[p.name] = {
                    type: p.type,
                    value: ValueAdapter.toResource(p, ParameterConfig.VALUE)
                };
            });
            result.storedProcedureParameters = params;
        } else {
            result.storedProcedureParameters = ValueAdapter.toResource(
                model,
                SqlSink.WRITER_STORED_PROCEDURE_PARAMETERS
            );
        }
        // clear [] to keep the current behavior
        if (
            result.storedProcedureParameters &&
            Array.isArray(result.storedProcedureParameters) &&
            result.storedProcedureParameters.length === 0
        ) {
            result.storedProcedureParameters = undefined;
        }
    }

    private _storedProcedureToModel(resource: Model.SqlSink, result: SqlSink, owner: Activity): void {
        ValueAdapter.toModel(result, SqlSink.WRITER_STORED_PROCEDURE_NAME, resource.sqlWriterStoredProcedureName);
        ValueAdapter.toModel(result, SqlSink.WRITER_TABLE_TYPE, resource.sqlWriterTableType);
        ValueAdapter.toModel(
            result,
            SqlSink.WRITER_TABLE_TYPE_PARAMETER_NAME,
            resource.storedProcedureTableTypeParameterName
        );
        result.storedProcedureParameters = [];

        if (resource.storedProcedureParameters != null) {
            if (
                typeof resource.storedProcedureParameters === 'object' &&
                resource.storedProcedureParameters.type === 'Expression'
            ) {
                ValueAdapter.toModel(
                    result,
                    SqlSink.WRITER_STORED_PROCEDURE_PARAMETERS,
                    resource.storedProcedureParameters
                );
            } else {
                Object.keys(resource.storedProcedureParameters).forEach((key) => {
                    const item = Owned.create<ParameterConfig>(ParameterConfig.MODEL, owner);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const originItem = resource.storedProcedureParameters as any;
                    item.name = key;
                    item.type = originItem[key] != null ? originItem[key].type : undefined;
                    ValueAdapter.toModel(
                        item,
                        ParameterConfig.VALUE,
                        originItem[key] != null ? originItem[key].value : undefined
                    );

                    if (result.storedProcedureParameters != null) {
                        result.storedProcedureParameters.push(item);
                    }
                });
            }
        }
    }

    private _upsertToModel(resource: Model.SqlSink, result: SqlSink): void {
        if (resource.upsertSettings) {
            // for copy-wizard generated activities, where upsertSettings parameterized as one
            if ((resource.upsertSettings as SharedModel.ExpressionValue)?.type === 'Expression') {
                ValueAdapter.toModel(result, SqlSink.UPSERT_SETTINGS, resource.upsertSettings);
                delete result.writeBehaviorUseTempDB;
            } else {
                const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
                ValueAdapter.toModel(
                    result,
                    SqlSink.WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME,
                    upsertSettings.interimSchemaName
                );
                ValueAdapter.toModel(result, SqlSink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
                ValueAdapter.toModel(result, SqlSink.WRITE_BEHAVIOR_USE_TEMP_DB, upsertSettings.useTempDB);
            }
        }
    }
}
